package com.karyadina.cloudsync.service

import com.karyadina.cloudsync.model.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.encodeToString
import kotlinx.serialization.decodeFromString
import com.karyadina.service.AppStorage
import com.karyadina.cloudsync.util.TimeUtil

/**
 * Sync engine with intelligent merge and deletion awareness.
 */
class SyncEngineImpl(
    private val googleDriveService: GoogleDriveService,
    private val cryptoService: CryptoService,
    private val googleAuthService: GoogleAuthService
) : SyncEngine {
    
    private val _pendingSyncItems = MutableStateFlow<List<PendingSyncItem>>(emptyList())
    override val pendingSyncItems: StateFlow<List<PendingSyncItem>> = _pendingSyncItems.asStateFlow()
    
    private val _pendingSizeInMiB = MutableStateFlow(0.0)
    override val pendingSizeInMiB: StateFlow<Double> = _pendingSizeInMiB.asStateFlow()
    
    private val _syncStatus = MutableStateFlow<SyncStatus>(SyncStatus.Idle)
    override val syncStatus: StateFlow<SyncStatus> = _syncStatus.asStateFlow()

    private val ARRAY_KEYS = setOf(
        "tasks", 
        "quick_notes", 
        "study_sessions", 
        "study_subjects", 
        "flashcard_subjects",
        "flashcard_units",
        "flashcard_cards",
        "flashcard_sessions",
        "pomodoro_sessions"
    )

    private val KEY_LAST_SYNC_TIME = "pref_last_sync_time"
    
    override suspend fun queueItem(itemId: String, itemType: String, size: Long, operation: SyncOperation): Result<Unit> = Result.success(Unit)
    
    override suspend fun executeSync(): Result<SyncResult> {
        _syncStatus.value = SyncStatus.Syncing
        val startTime = TimeUtil.currentTimeMillis()
        val localLastSyncTime = AppStorage.get().loadString(KEY_LAST_SYNC_TIME)?.toLongOrNull() ?: 0L
        
        return try {
            val localData = AppStorage.get().exportAll()
            
            var cloudData: Map<String, String>? = null
            var snapshotTime = 0L
            var oldSnapshotIds = mutableListOf<String>()
            
            val filesResult = googleDriveService.listFiles()
            if (filesResult.isSuccess) {
                val snapshotFiles = filesResult.getOrThrow()
                    .filter { it.fileName == "cloudsync_data.enc" }
                    .sortedByDescending { it.modifiedTime }
                
                val newestSnapshot = snapshotFiles.firstOrNull()
                if (newestSnapshot != null) {
                    snapshotTime = newestSnapshot.modifiedTime
                    val downloadResult = googleDriveService.downloadFile(newestSnapshot.fileId)
                    if (downloadResult.isSuccess) {
                        val decryptedResult = cryptoService.decrypt(downloadResult.getOrThrow())
                        if (decryptedResult.isSuccess) {
                            val jsonString = decryptedResult.getOrThrow().decodeToString()
                            cloudData = Json.decodeFromString<Map<String, String>>(jsonString)
                        }
                    }
                    oldSnapshotIds.addAll(snapshotFiles.map { it.fileId })
                }
            }

            // Perform Deletion-Aware Merge
            val mergedData = if (cloudData != null) {
                mergeData(localData, cloudData!!, localLastSyncTime, snapshotTime)
            } else {
                localData
            }

            // OPTIMIZATION: If merged data is identical to local data, and cloud exists, skip upload
            if (cloudData != null && mergedData == localData && localData == cloudData) {
                println("CloudSync: Local and Cloud are in sync. Skipping upload.")
                AppStorage.get().saveString(KEY_LAST_SYNC_TIME, TimeUtil.currentTimeMillis().toString())
                _syncStatus.value = SyncStatus.Success(TimeUtil.currentTimeMillis())
                return Result.success(SyncResult(true, mergedData.size, emptyList(), 0))
            }

            // Apply merged data
            AppStorage.get().importAll(mergedData)
            AppStorage.get().saveString(KEY_LAST_SYNC_TIME, TimeUtil.currentTimeMillis().toString())
            AppStorage.notifyRefresh()

            // Upload new snapshot
            val jsonBytes = Json.encodeToString(mergedData).encodeToByteArray()
            val encryptedResult = cryptoService.encrypt(jsonBytes)
            if (encryptedResult.isSuccess) {
                val uploadResult = googleDriveService.uploadFile("cloudsync_data.enc", encryptedResult.getOrThrow())
                if (uploadResult.isSuccess) {
                    oldSnapshotIds.forEach { googleDriveService.deleteFile(it) }
                    _syncStatus.value = SyncStatus.Success(TimeUtil.currentTimeMillis())
                    return Result.success(SyncResult(true, mergedData.size, emptyList(), TimeUtil.currentTimeMillis() - startTime))
                }
            }
            Result.failure(Exception("Sync failed during upload"))
        } catch (e: Exception) {
            _syncStatus.value = SyncStatus.Error(e.message ?: "Unknown error")
            Result.failure(e)
        }
    }

    private fun mergeData(local: Map<String, String>, cloud: Map<String, String>, lastSync: Long, snapshotTime: Long): Map<String, String> {
        val merged = mutableMapOf<String, String>()
        val allKeys = local.keys + cloud.keys
        
        for (key in allKeys) {
            val localVal = local[key]
            val cloudVal = cloud[key]
            
            when {
                cloudVal == null -> if (localVal != null) merged[key] = localVal
                localVal == null -> {
                    // Item exists in cloud but not locally. 
                    if (lastSync > 0 && snapshotTime < lastSync) {
                        // deleted locally
                    } else {
                        merged[key] = cloudVal
                    }
                }
                key in ARRAY_KEYS -> merged[key] = mergeJsonArrays(localVal, cloudVal, lastSync, snapshotTime)
                else -> merged[key] = localVal // Local preferences win
            }
        }
        return merged
    }

    private fun mergeJsonArrays(localJson: String, cloudJson: String, lastSync: Long, snapshotTime: Long): String {
        val localArray = try { Json.parseToJsonElement(localJson).jsonArray } catch(e: Exception) { JsonArray(emptyList()) }
        val cloudArray = try { Json.parseToJsonElement(cloudJson).jsonArray } catch(e: Exception) { JsonArray(emptyList()) }
        
        val localById = localArray.associateBy { it.jsonObject["id"]?.jsonPrimitive?.content ?: "" }
        val cloudById = cloudArray.associateBy { it.jsonObject["id"]?.jsonPrimitive?.content ?: "" }
        
        val mergedMap = mutableMapOf<String, kotlinx.serialization.json.JsonElement>()

        // 1. Process local items: detect if they were deleted in the cloud
        for ((id, localEl) in localById) {
            val cloudEl = cloudById[id]
            if (cloudEl != null) {
                // Both exist, will merge in step 2
            } else {
                // Missing in cloud. 
                // If it was created/updated AFTER the cloud snapshot was made, it's new here. Keep it.
                // If it's OLDER than the cloud snapshot, it means it was deleted on another device.
                if (getTimestamp(localEl) > snapshotTime) {
                    mergedMap[id] = localEl
                }
            }
        }

        // 2. Process cloud items: detect if they were deleted locally
        for ((id, cloudEl) in cloudById) {
            val localEl = localById[id]
            if (localEl == null) {
                // Missing locally.
                // If it's newer than our last sync, it's a new item from another device.
                // If it's older than our last sync, we must have deleted it here.
                if (getTimestamp(cloudEl) > lastSync || lastSync == 0L) {
                    mergedMap[id] = cloudEl
                }
            } else {
                // Both exist, take the newest one
                if (getTimestamp(cloudEl) > getTimestamp(localEl)) {
                    mergedMap[id] = cloudEl
                } else {
                    mergedMap[id] = localEl
                }
            }
        }
        
        return Json.encodeToString(JsonArray(mergedMap.values.toList()))
    }

    private fun getTimestamp(element: kotlinx.serialization.json.JsonElement): Long {
        val obj = element.jsonObject
        return obj["updatedAt"]?.jsonPrimitive?.content?.toLongOrNull()
            ?: obj["createdAt"]?.jsonPrimitive?.content?.toLongOrNull() ?: 0L
    }

    override suspend fun clearPendingQueue() = Result.success(Unit)
    override fun estimateSyncTime() = 0L
    override fun getSelectiveSyncPreferences() = com.karyadina.cloudsync.model.SelectiveSyncPreferences(emptyMap(), emptySet())
    override suspend fun setSelectiveSyncPreferences(prefs: com.karyadina.cloudsync.model.SelectiveSyncPreferences) = Result.success(Unit)
}
